import { Component, inject, Input } from '@angular/core';
import {
    BlockCardComponent,
    CardType,
    ConfigService,
    PageType,
    ZoneService,
    ZoneType,
} from '@gallaghercp/core-frontend';

@Component({
    selector: 'core-client-block-card',
    templateUrl: './block-card.component.html',
    standalone: false,
})
export class BlockClientCardComponent extends BlockCardComponent {
    @Input() zone?: string;
    @Input() parentUrl?: string;
    @Input() pageGradient?: string;
    @Input() override imageType?: string;
    @Input() override imageAlign?: string;
    @Input() showTitleArrow?: boolean;
    @Input() cardBackgroundColor?: string;
    @Input() cardBoxShadowColor?: string;
    @Input() cardPrimaryHeader?: boolean;
    @Input() cardTitleColor?: string;
    @Input() cardTitleFontSize?: string;
    @Input() cardTitleFontWeight?: string;
    @Input() cardLinkText?: string;
    // New optional CTA icon inputs (mirrors pullout block capabilities)
    @Input() includeCtaIcon?: boolean;
    @Input() buttonIcon?: string; // free-form label from CMS (e.g., "External link", "Download", etc.)
    // Optional link color override for CTA text and icon
    @Input() cardLinkColor?: string;

    private readonly zoneService = inject(ZoneService);

    override get isIconImage(): boolean {
        return this.imageType?.toLowerCase() === 'icons';
    }

    override get iconImageAlignment(): 'left' | 'center' | 'right' | undefined {
        const alignment = this.imageAlign?.toLowerCase();

        return alignment === 'left' || alignment === 'center' || alignment === 'right' ? alignment : undefined;
    }

    override ngOnInit(): void | false {
        super.ngOnInit();
        this.imageUrl = this.imageService.getImageUrl(this.card, 'card');
    }

    override cardNameId(card: CardType) {
        return 'card-' + card.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, '-');
    }

    cardTitlePrefix(card: CardType): string {
        const title = card.name.trim();
        const lastSpace = title.lastIndexOf(' ');

        return lastSpace === -1 ? '' : `${title.slice(0, lastSpace)} `;
    }

    cardTitleEnd(card: CardType): string {
        const title = card.name.trim();
        const lastSpace = title.lastIndexOf(' ');

        return lastSpace === -1 ? title : title.slice(lastSpace + 1);
    }

    fullUrl(card: CardType): string {
        let zoneUrl = '';
        const zoneRouting = this.configService.get('zoneRouting');
        if (
            Array.isArray(zoneRouting) &&
            zoneRouting.length &&
            this.zoneService.getCurrentZone() &&
            (this.zoneService.getCurrentZone() as ZoneType).zone > 1
        ) {
            zoneUrl = (this.zoneService.getCurrentZone() as ZoneType).code + '/';
        }

        if (card.url) {
            // Handling for when we're on a default zone url, where zoneUrl and currentPage.url are the same
            if (zoneUrl === card.url + '/') {
                return card.url || '';
            }

            return '/' + zoneUrl + card.url;
        }

        return this.url ?? `/${card.url}`;
    }

    // Normalizes color coming from CMS so tokens like "primary" resolve to usable CSS values.
    // Accepted inputs:
    //  - Hex/RGB(A): "#005EB8", "rgb(0,0,0)", "rgba(0,0,0,.6)" → returned as-is
    //  - CSS variables: "var(--color-primary)" → returned as-is
    //  - Tokens: "primary", "secondary", "accent", "link" → mapped to design system variables
    //  - Unknown/empty → undefined (no inline color applied)
    private normalizeColor(value?: string): string | undefined {
        const v = (value || '').trim();
        if (!v) return undefined;

        const lower = v.toLowerCase();

        // Pass through if it looks like a direct CSS color
        if (
            lower.startsWith('#') ||
            lower.startsWith('rgb(') ||
            lower.startsWith('rgba(') ||
            lower.startsWith('hsl(') ||
            lower.startsWith('hsla(')
        ) {
            return v;
        }
        if (lower.startsWith('var(')) {
            return v;
        }

        // Simple token mapping to CSS variables used by the design system
        switch (lower) {
            case 'primary':
                return 'var(--color-primary)';
            case 'secondary':
                return 'var(--color-secondary)';
            case 'secondary-2':
                return 'var(--color-secondary-2)';
            default:
                return undefined;
        }
    }

    get resolvedLinkColor(): string | undefined {
        return this.normalizeColor(this.cardLinkColor);
    }
}
